/**
 * Peer-to-peer message thread for a selected network contact.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider } from "@mui/material";

import { VAvatar, VFroala, VText } from "form";
import { useAsterController } from "context";
import { actionChatTyping, actionPeerChatAdd, actionPeerChatList } from "context/action";
import ChatTypingDots, { htmlHasText } from "pages/chat/chat.typing";
import SafeHtml from "pages/common/SafeHtml";
import { getDateObj, getShortTime } from "utils/date";
import { asArray, asBool, asNumber, asObject, asString } from "utils/safeData";

/**
 * Converts a message timestamp into minutes since epoch for grouping.
 *
 * @param {string|number} value Created-at value.
 * @returns {number}
 */
const minutesFromCreated = (value) => {
  const date = getDateObj(value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() / (1000 * 60) : 0;
};

/**
 * Facebook-style conversation with a classmate or coach.
 *
 * @param {{ peer: object, height?: number }} props Selected contact and viewport height.
 */
const NetworkThread = ({ peer, height: rawHeight }) => {
  const { t } = useTranslation("common");
  const [controller, dispatch] = useAsterController();
  const user = asObject(controller?.user);
  const peerChat = asObject(controller?.peerChat);
  const chatTyping = asObject(controller?.chatTyping);
  const isChatEnabled = asBool(controller?.isChatEnabled, true);
  const height = asNumber(rawHeight, 500);

  const [model, setModel] = useState("");
  const peerId = peer?.user_id;
  const displayName = asString(peer?.fullname) || asString(peer?.username) || "-";
  const eventId = `${process.env.REACT_APP_SITE_NAME}_${user.id}_${peerId}`;

  useEffect(() => {
    if (!peerId) return undefined;
    dispatch({ type: "SET_PEER_CHAT_ACTIVE", value: true });
    setModel("");
    actionPeerChatList(dispatch, { peer_id: peerId, page: 0, page_size: 40 });
    return () => dispatch({ type: "SET_PEER_CHAT_ACTIVE", value: false });
  }, [dispatch, peerId]);

  useEffect(() => {
    if (!peerId || !user.id) return undefined;
    const typing = htmlHasText(model);
    let startTimer;
    let stopTimer;
    if (typing && isChatEnabled) {
      startTimer = window.setTimeout(() => actionChatTyping(dispatch, eventId, true), 250);
      stopTimer = window.setTimeout(() => actionChatTyping(dispatch, eventId, false), 1800);
    } else {
      actionChatTyping(dispatch, eventId, false);
    }
    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(stopTimer);
    };
  }, [model, peerId, user.id, isChatEnabled, dispatch, eventId]);

  const messages = useMemo(() => {
    const next = asArray(peerChat.messages).map((msg) => asObject(msg));
    if (!next.length) return [];
    for (let i = 1; i < next.length; i++) {
      const sameSender = Number(next[i].from_id) === Number(next[i - 1].from_id);
      next[i - 1].show_date = !sameSender;
      const currMins = minutesFromCreated(next[i - 1].created);
      const prevMins = minutesFromCreated(next[i].created);
      next[i - 1].show_date = !next[i - 1].show_date && currMins - prevMins > 10 ? true : next[i - 1].show_date;
    }
    next[next.length - 1].show_date = true;
    return next;
  }, [peerChat.messages]);

  const showTyping =
    asBool(chatTyping.typing) &&
    String(chatTyping.room || "").split("_").includes(String(peerId)) &&
    Number(chatTyping.from_id) === Number(peerId);

  /**
   * Sends a peer message.
   *
   * @param {string} value Sanitized HTML body.
   * @returns {void}
   */
  const onSubmit = (value) => {
    const currTime = new Date().toISOString();
    actionPeerChatAdd(dispatch, {
      user_id: user.id,
      peer_id: peerId,
      from_id: user.id,
      text: asString(value),
      created: currTime,
      updated: currTime,
    });
    actionChatTyping(dispatch, eventId, false);
  };

  if (!peerId) {
    return (
      <Box className="flex-1 flex items-center justify-center p-8">
        <VText color="secondary">{t("Select a contact to message")}</VText>
      </Box>
    );
  }

  return (
    <Box className="relative w-full border-0 border-t border-solid border-gray-300 dark:border-gray-500 v-chat-content">
      <Box className="p-6 hidden md:flex items-center h-[88px] gap-3">
        <Box className="relative">
          <VAvatar src={asString(peer.avatar)} bgColor="light" className="w-12 h-12" />
          <Box className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${peer.online ? "bg-green-500" : "bg-gray-300"}`} />
        </Box>
        <Box>
          <VText className="text-2xl text-limit-2">{displayName}</VText>
          <VText color="secondary" className="text-xs">
            {peer.online ? t("Online") : t("Offline")}
          </VText>
        </Box>
      </Box>

      <Divider className="bg-gray-200 dark:bg-gray-500" />

      <Box height={height} className="flex flex-col">
        <Box
          className="flex-1 w-full overflow-y-auto v-light-scrollbar px-3 pb-3 flex flex-col-reverse"
          sx={{ maxHeight: Math.max(height - 130, 0) }}
          id="v-network-chat"
        >
          {showTyping ? <ChatTypingDots /> : null}
          {messages.map((msg) => {
            const mine = Number(msg.from_id) === Number(user.id);
            return mine ? (
              <Box
                key={String(msg.created)}
                className={`flex flex-col w-3/4 items-end ml-auto ${msg.show_date ? "mt-3" : "mt-1"}`}
              >
                <VText color="secondary" className={`text-sm ${msg.show_date ? "" : "hidden"}`}>
                  You, {getShortTime(msg.created)}
                </VText>
                <Box className="v-chat-bubble v-chat-bubble--sent">
                  <VText color="custom">
                    <SafeHtml html={asString(msg.text)} />
                  </VText>
                </Box>
              </Box>
            ) : (
              <Box key={String(msg.created)} className={`flex w-3/4 ${msg.show_date ? "mt-3" : "mt-1"}`}>
                <VAvatar
                  src={asString(peer.avatar)}
                  bgColor="light"
                  className={`w-10 h-10 ${msg.show_date ? "visible" : "invisible"}`}
                />
                <Box className="ml-3">
                  <VText color="secondary" className={`text-sm ${msg.show_date ? "" : "hidden"}`}>
                    {displayName}, {getShortTime(msg.created)}
                  </VText>
                  <Box className="v-chat-bubble v-chat-bubble--recv">
                    <VText color="custom">
                      <SafeHtml html={asString(msg.text)} />
                    </VText>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Divider className="bg-gray-200 dark:bg-gray-700" />
        <Box className="v-chat-froala relative v-chat-dark">
          <VFroala isChat isEnterSubmit onSubmit={onSubmit} model={asString(model)} setModel={setModel} disabled={!isChatEnabled} />
        </Box>
      </Box>
    </Box>
  );
};

export default NetworkThread;
